<template>
    <div class="reset_password_container">
        <p class="reset_title">{{ $t('common.resetPasswordNow') }}</p>
        <!-- Password -->
        <InputField v-model="newPassword" :isShowError="passwordValidationNotice" @valueChanged="passwordChange"
            :isPassword="true" :labelText="$t('loginRegister.enterPassword')" fieldType="password" />

        <div v-if="passwordValidationNotice" class="errorNotice">
            <van-icon name="warning-o" />
            {{ $t('loginRegister.passwordForamt') }}
        </div>

        <!-- Confirm Password -->
        <InputField v-model="confirmPassword" :isShowError="confirmPasswordValidationNotice || passwordMismatch"
            @valueChanged="confirmPasswordChange" :isPassword="true" :labelText="$t('loginRegister.confirmPassword')"
            fieldType="password" />

        <!-- Confirm format error -->
        <div v-if="confirmPasswordValidationNotice" class="errorNotice">
            <van-icon name="warning-o" />
            {{ $t('loginRegister.passwordForamt') }}
        </div>

        <!-- Password mismatch error -->
        <div v-if="passwordMismatch" class="errorNotice">
            <van-icon name="warning-o" />
            {{ $t('loginRegister.differentPassword') }}
        </div>

        <MainButton :title="$t('common.resetPasswordNow')" radius="5px !important"
            :activeStatus="isFormValid" @click="resetPassword" btn-padding="30px !important"
            style="margin: 2rem 0 1rem;" :isBtnLoading="btnLoading" />
    </div>
    <StatusPopup ref="statusRef" :is-show-success="isSuccess" :content="toastContent" />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import InputField from '../inputField.vue';
import { resetPassword as resetPasswordApi } from '@/api/user';
import { useI18n } from 'vue-i18n';
import MainButton from '@/themes/themeThree/components/common/mainButton.vue';
import StatusPopup from '@/components/common/statusPopup.vue';

const { t } = useI18n();
const passwordValidationNotice = ref(false)
const confirmPasswordValidationNotice = ref(false)
const isSuccess = ref(false)
const toastContent = ref('')
const statusRef = ref()
const btnLoading = ref(false)
const props = defineProps({
    uuid: {
        type: String,
        default: ''
    },
    contact: {
        type: String,
        default: ''
    },
    contactType: {
        type: String,
        default: ''
    },
    areaCode: {
        type: String,
        default: ''
    },
    fpUuid: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['reset']);

const newPassword = ref('');
const confirmPassword = ref('');

const resetPassword = () => {
    const params = {
        contact: props.contact,
        contactType: props.contactType,
        areaCode: props.areaCode,
        newPassword: newPassword.value.trim(),
        confirmPassword: confirmPassword.value.trim(),
        fpUuid: props.fpUuid
    };
    btnLoading.value = true;
    resetPasswordApi(params)
        .then(res => {
            if (res.code === 200) {
                showSuccess(res.msg);
                setTimeout(() => {
                    emit('reset');
                }, 500);
            } else {
                showError(res.msg)
            }
            btnLoading.value = false;
        })
        .catch(err => {
            console.error('Reset password error:', err);
            showError(err)
            btnLoading.value = false;
        });
};

const checkPasswordValidation = (value: string) => {
  if (value.length === 0) {
    passwordValidationNotice.value = false
    return
  }

  passwordValidationNotice.value =
    !(value.length >= 8 && value.length <= 20)
}

const checkConfirmPasswordValidation = (value: string) => {
  if (value.length === 0) {
    confirmPasswordValidationNotice.value = false
    return
  }

  confirmPasswordValidationNotice.value =
    !(value.length >= 8 && value.length <= 20)
}

const passwordMismatch = computed(() => {
  return (
    confirmPassword.value.length > 0 &&
    newPassword.value.length > 0 &&
    newPassword.value !== confirmPassword.value
  )
})

const passwordChange = (value: string) => {
  newPassword.value = value
  checkPasswordValidation(value)
  checkConfirmPasswordValidation(confirmPassword.value)
}

const confirmPasswordChange = (value: string) => {
  confirmPassword.value = value
  checkConfirmPasswordValidation(value)
}

const isFormValid = computed(() => {
  return (
    newPassword.value.length >= 8 &&
    newPassword.value.length <= 20 &&
    confirmPassword.value.length >= 8 &&
    confirmPassword.value.length <= 20 &&
    newPassword.value === confirmPassword.value
  )
})

const showError = (message: string) => {
    toastContent.value = message;
    isSuccess.value = false;
    statusRef.value.open()
}

const showSuccess = (message: string) => {
    toastContent.value = message;
    isSuccess.value = true;
    statusRef.value.open()
}

</script>

<style scoped>
.reset_password_container {
    padding: 0px 0 20px;
}

.reset_title {
    color: #ffffff66;
    font-size: 15px;
    margin: 0 5px;
    font-weight: 500;
}

.reset_password_container :deep(.input_container) {
    margin-bottom: 15px;
    margin-top: 15px;
}

.reset_btn {
    background: #22c1b0;
    color: #fff;
    border: none;
    padding: 12px 24px;
    border-radius: 5px;
    margin-top: 20px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: background 0.3s;
    width: 100%;
}

.reset_btn:hover {
    background: #1ba89a;
}

.errorNotice {
    color: #E00000;
    font-size: 15px;
    padding: 0 10px;
    font-weight: 500;
}
</style>